import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { Box, Typography, Chip, Card, Button, CardMedia } from '@mui/material';
import NativeOrWeb from '../../../../utils/native';
import ShareIcon from '@mui/icons-material/Share';
import { trackDirect } from '../../../../store/analytics/api';
import { tracking } from '../../../../utils/tracking';
import CustomizeHotTopicButton from './CustomizeHotTopicButton';

const useStyles = makeStyles()(theme => ({
  card: {
    padding: '1rem',
    margin: '1rem 16px'
  },
  content: {
    flex: 1,
    padding: '1rem'
  },
  '@global': {
    '.MuiTypography-root': {
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap'
    }
  },
  title: {
    color: '#111618',
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.25,
    textAlign: 'left',
    paddingBottom: '0.5rem',
    paddingTop: '1.25rem',
    margin: 0
  },
  meta: {
    color: '#617c89',
    fontSize: '1.6rem',
    fontWeight: 400,
    lineHeight: 'normal',
    paddingBottom: '0.75rem',
    paddingTop: '0.25rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    margin: 0
  },
  imageContainer: {
    marginBottom: '0.5rem',
    alignItems: 'center',
    justifyContent: 'center'
  },
  imageWrapper: {
    margin: 'auto',
    [theme.breakpoints.up(480)]: {
      paddingLeft: '1rem',
      paddingRight: '1rem',
      paddingTop: '0.75rem',
      paddingBottom: '0.75rem'
    }
  },
  image: {
    borderRadius: '0.5rem',
    width: '100%',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    [theme.breakpoints.up(480)]: {
      borderRadius: '0.5rem'
    }
  },
  summary: {
    color: '#111618',
    fontSize: '1.71rem',
    fontWeight: 400,
    lineHeight: 'normal',
    paddingBottom: '1rem',
    paddingTop: '0.25rem',
    margin: 0
  },
  sectionTitle: {
    color: '#717171',
    fontSize: '1.6rem',
    fontWeight: 500,
    paddingBottom: '0.75rem',
    paddingTop: '1rem',
    marginBottom: '0.75rem',
    borderBottom: '1px solid #E0E0E0'
  },
  sectionTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    flexWrap: 'wrap',
    color: '#717171',
    fontSize: '1.6rem',
    fontWeight: 500,
    paddingBottom: '0.75rem',
    paddingTop: '1rem',
    marginBottom: '0.75rem',
    borderBottom: '1px solid #E0E0E0'
  },
  sectionTitleText: {
    flex: 1,
    minWidth: 0
  },
  tags: {
    display: 'flex',
    gap: '0.75rem',
    padding: '0.75rem',
    flexWrap: 'wrap',
    paddingRight: '1rem'
  },
  tag: {
    height: '2rem',
    flexShrink: 0,
    borderRadius: '0.5rem',
    backgroundColor: '#f0f3f4',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    '& .MuiChip-label': {
      color: '#111618',
      fontSize: '1.6rem',
      fontWeight: 500,
      lineHeight: 'normal',
      padding: 0
    }
  },
  text: {
    color: '#111618',
    fontSize: '1.6rem',
    fontWeight: 400,
    lineHeight: 'normal',
    paddingBottom: '0.75rem',
    paddingTop: '0.25rem',
    margin: 0
  },
  interactionCardContent: {
    display: 'flex',
    width: '100%',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '0.8rem',
    marginBottom: '1rem'
  },
  interactionCardText: {
    color: '#000000',
    fontSize: '1.6rem',
    flex: 1,
    minWidth: 0,
    margin: 0
  },
  readOriginalLink: {
    color: '#617c89',
    fontSize: '1.37rem',
    paddingLeft: '0rem',
    paddingRight: '1rem',
    fontWeight: 400,
    lineHeight: 'normal',
    textTransform: 'none',
    '&:focus': {
      outline: 'none',
      backgroundColor: 'transparent'
    },
    '&:active': {
      outline: 'none',
      backgroundColor: 'transparent'
    },
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline'
    },
    '&.Mui-focusVisible': {
      outline: 'none',
      backgroundColor: 'transparent'
    }
  },
  shareButtonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: 'auto'
    // width: '2.5rem',
    // height: '2.5rem',
  },
  shareButton: {
    height: '32px',
    color: '#3966f8',
    fontSize: '1.5rem',
    textTransform: 'none',
    borderColor: 'transparent',
    minWidth: '32px',
    padding: '0 4px'
  },
  readOriginalLinkContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%'
  }
}));

const formatDate = timestamp => {
  const date = new Date(timestamp);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${month}月${day}日 ${hours}:${minutes}`;
};

const formatMultiLineText = (text, classes) => {
  if (!text) return '';
  if (Array.isArray(text))
    return text.map((line, index) => <Typography key={index} className={classes?.text}>{`• ${line}`}</Typography>);

  return text
    .split('\n')
    .map((line, index) => <Typography key={index} className={classes?.text}>{`• ${line}`}</Typography>);
};

const formatInteractionFocus = (focus, classes) => {
  if (!focus) return '';
  const points = [];
  if (focus.general_attention) points.push(focus.general_attention);
  if (focus.daily_life_connection) points.push(focus.daily_life_connection);
  if (focus.interesting_fact) points.push(focus.interesting_fact);
  return points.map((text, index) => <Typography key={index} className={classes?.text}>{`• ${text}`}</Typography>);
};

const formatInteractionStrategy = (strategies, classes) => {
  if (!strategies || !Array.isArray(strategies)) return '';
  return strategies.map((s, index) => (
    <Typography key={index} className={classes?.text}>{`• ${s.principle}: ${s.application}`}</Typography>
  ));
};
const InteractionScriptCard = ({ script, shareText, newsUrl, intl, newsId, contentType = 'generated-content' }) => {
  const { classes } = useStyles();
  // onCopy show native share modal
  const onShare = () => {
    // const shareText = `${script}\n${newsUrl}`;
    // NativeOrWeb.copyToClipboard(shareText);
    // toast.success(IconToast('info', intl.formatMessage({ id: 'selected-newsfeed-interaction-copied-msg' })), {
    //   className: 'new-toast'
    // });
    tracking('Click on Selected Newsfeed', { newsId: newsId, content: script, 'content-type': contentType });
    trackDirect('shareSelectedNewsfeed', newsId);
    NativeOrWeb.share({ text: `${script}\n${newsUrl}` }).catch(error => {
      console.error(error);
    });
  };

  const shareButton = (
    <Button
      variant="outlined"
      className={classes.shareButton}
      size="small"
      disableRipple
      disableFocusRipple
      onClick={onShare}
    >
      <ShareIcon style={{ fontSize: 'inherit' }} />
    </Button>
  );
  return (
    <Box className={classes.interactionCardContent}>
      <Typography className={classes.interactionCardText}>{script}</Typography>
      {shareButton}
    </Box>
  );
};

const NewsfeedCardCBB = ({ news, intl, history, newsfeedId }) => {
  const { classes } = useStyles();
  const audio = news.audio;

  return (
    <Card className={classes.card}>
      <Box className={classes.content}>
        <Typography variant="h4" className={classes.title}>
          {news.title}
        </Typography>
        <Typography className={classes.meta}>
          📰 {news.source} | {formatDate(news.timestamp)}
        </Typography>

        <Box className={classes.imageContainer}>
          <Box
            className={classes.imageWrapper}
            sx={{
              width: 500,
              maxWidth: '100%'
            }}
          >
            <CardMedia
              component="img"
              className={classes.image}
              image={news.images?.image_url_pfp || news.images?.image_url_poe}
              title={news.title}
            />
          </Box>
        </Box>
        {audio && <audio src={audio} controls style={{ width: '100%' }} />}

        <Typography className={classes.summary}>
          {news.summary}
          <Button
            variant="text"
            color="secondary"
            href={news.raw?.link}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.readOriginalLink}
            disableRipple
            disableFocusRipple
          >
            {intl.formatMessage({ id: 'selected-newsfeed-read-original-link-hardcode' })}
          </Button>
        </Typography>

        <Typography className={classes.sectionTitle}>
          {intl.formatMessage({ id: 'selected-newsfeed-target-clients-title' })}
        </Typography>
        <Box className={classes.text}>{formatMultiLineText(news.interaction_most_fit, classes)}</Box>

        <Typography className={classes.sectionTitle}>
          {intl.formatMessage({ id: 'selected-newsfeed-interaction-focus-title' })}
        </Typography>
        <Box className={classes.text}>{formatInteractionFocus(news.interaction_focus, classes)}</Box>

        <Box className={classes.sectionTitle}>
          {intl.formatMessage({ id: 'selected-newsfeed-interaction-strategy-title' })}
        </Box>
        <Box className={classes.text}>{formatInteractionStrategy(news.interaction_strategy, classes)}</Box>

        <Box className={classes.sectionTitleRow}>
          <Box className={classes.sectionTitleText}>
            {intl.formatMessage({ id: 'selected-newsfeed-interaction-script-title' })}
          </Box>
          <CustomizeHotTopicButton news={news} intl={intl} history={history} newsfeedId={newsfeedId} />
        </Box>

        {news.client_messages?.caring && (
          <InteractionScriptCard
            intl={intl}
            script={news.client_messages.caring}
            shareText={intl.formatMessage({ id: 'selected-newsfeed-interaction-script-share-text' })}
            newsUrl={news.raw?.link}
          />
        )}

        {news.client_messages?.humorous && (
          <InteractionScriptCard
            intl={intl}
            script={news.client_messages.humorous}
            shareText={news.client_messages.humorous}
            newsUrl={news.raw?.link}
            newsId={news.id}
          />
        )}

        <InteractionScriptCard
          contentType="hardcoded-content"
          intl={intl}
          script={intl.formatMessage({ id: 'selected-newsfeed-hardcoded-share-text' })}
          shareText={intl.formatMessage({ id: 'selected-newsfeed-hardcoded-share-text' })}
          newsUrl={news.raw?.link}
          newsId={news.id}
        />
        {/* <Box className={classes.readOriginalLinkContainer}> */}
        {/* <Button
            variant="text"
            color="secondary"
            href={news.raw?.link}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.readOriginalLink}
            disableRipple
            disableFocusRipple
          >
            {intl.formatMessage({ id: 'selected-newsfeed-hardcoded-share-text' })}
          </Button> */}
        {/* <Button
            variant="outlined"
            className={classes.shareButton}
            size="small"
            onClick={() => {
              const newsId = news.id;
              tracking('Click on Selected Newsfeed - Share', { newsId, text: "script", 'content-type': 'hardcoded-content' });
              trackDirect('shareSelectedNewsfeed', newsId);
              NativeOrWeb.share({ text: `${news.title}\n${news.raw?.link}` }).catch(error => {
                console.error(error);
              })
            }}
          >
            <ShareIcon style={{ fontSize: 'inherit' }} />
          </Button> */}
        {/* </Box> */}
      </Box>
    </Card>
  );
};

export default NewsfeedCardCBB;
